import { useContext, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Header,
  Icon,
  Link,
  Modal,
  Popover,
  SpaceBetween,
  StatusIndicator,
  Table,
  Toggle,
} from "@amzn/awsui-components-react";
import { useCollection } from "@amzn/awsui-collection-hooks";
import { MatchSetData } from "../../hooks/useFetchMatchSets";
import { MatchSetInfo } from "../../types/health";
import { MatchSetState, MatchKeyType } from "../../types/matchSetState";
import { createFlashUIMatcherLink } from "../../helpers/linkUtils";
import { useMarketplaceId } from "../../contexes/MarketplaceIdContext";
import { StageContext } from "../../contexes/OrderIdContext";
import { Stage } from "../../lusca-search-api/generated-src";
import { FLOW_TYPES } from "../../helpers/flowTypeUtils";
import { FLOW_PARAM } from "../../hooks/useFlowType";

interface Props {
  matchSetData: MatchSetData;
  isDualFlow?: boolean;
}

function stateIndicatorType(state: MatchSetState) {
  switch (state) {
    case MatchSetState.CLOSED:
      return "success";
    case MatchSetState.OPEN:
      return "warning";
    case MatchSetState.PENDING:
      return "pending";
    default:
      return "info";
  }
}

// Assign colors sequentially by first appearance to guarantee distinct values get distinct colors
const ACCOUNT_COLORS = [
  "blue",
  "green",
  "red",
  "severity-medium",
  "severity-low",
  "severity-high",
  "severity-critical",
] as const;
type AccountBadgeColor = (typeof ACCOUNT_COLORS)[number];

function buildColorMap(items: MatchSetInfo[]): Map<string, AccountBadgeColor> {
  const map = new Map<string, AccountBadgeColor>();
  let idx = 0;
  items.forEach((item) => {
    [item.accountNumber, item.companyCode].forEach((val) => {
      if (val && !map.has(val)) {
        map.set(val, ACCOUNT_COLORS[idx % ACCOUNT_COLORS.length]);
        idx++;
      }
    });
  });
  return map;
}

const COLUMN_DEFINITIONS = (
  marketplaceId: string,
  stage: Stage,
  colorMap: Map<string, AccountBadgeColor>
) => [
  {
    id: "matchKey",
    header: "Match Key",
    sortingField: "matchKey",
    cell: (item: MatchSetInfo) => (
      <div style={{ wordBreak: "break-all" }}>
        <Link
          href={createFlashUIMatcherLink(
            marketplaceId,
            item.matchKey,
            stage,
            item.accountNumber
          )}
          external
        >
          {item.matchKey}
        </Link>
      </div>
    ),
    width: 450,
  },
  {
    id: "state",
    header: "State",
    sortingField: "state",
    cell: (item: MatchSetInfo) => (
      <StatusIndicator type={stateIndicatorType(item.state)}>
        {item.state}
      </StatusIndicator>
    ),
    width: 120,
  },
  {
    id: "type",
    header: "Type",
    sortingField: "matchKeyType",
    cell: (item: MatchSetInfo) => {
      const label =
        item.matchKeyType === MatchKeyType.ROYALTY
          ? "Royalty"
          : item.matchKeyType === MatchKeyType.DEFERRAL
          ? "Deferral"
          : "Revenue";
      const color =
        item.matchKeyType === MatchKeyType.ROYALTY
          ? "text-status-inactive"
          : "text-body-secondary";
      return <Box color={color}>{label}</Box>;
    },
    width: 90,
  },
  {
    id: "account",
    header: "Account",
    sortingField: "accountNumber",
    cell: (item: MatchSetInfo) =>
      item.accountNumber ? (
        <Badge color={colorMap.get(item.accountNumber) ?? "blue"}>
          {item.accountNumber}
        </Badge>
      ) : (
        "-"
      ),
    width: 100,
  },
  {
    id: "companyCode",
    header: "CC",
    sortingField: "companyCode",
    cell: (item: MatchSetInfo) =>
      item.companyCode ? (
        <Badge color={colorMap.get(item.companyCode) ?? "blue"}>
          {item.companyCode}
        </Badge>
      ) : (
        "-"
      ),
    width: 75,
    minWidth: 75,
  },
  {
    id: "amounts",
    header: "Debits / Credits",
    cell: (item: MatchSetInfo) => {
      if (!item.amount) return "-";
      const cur = item.amount.currencyCode || "";
      return (
        <SpaceBetween direction="vertical" size="xxxs">
          <Box color="text-status-error">
            {item.numDebits ?? 0}× Debit: {item.amount.debitValue ?? "0"} {cur}
          </Box>
          <Box color="text-status-success">
            {item.numCredits ?? 0}× Credit: {item.amount.creditValue ?? "0"}{" "}
            {cur}
          </Box>
          <Box fontWeight="bold">
            Net: {item.amount.value} {cur}
          </Box>
        </SpaceBetween>
      );
    },
    width: 220,
  },
  {
    id: "date",
    header: "Accounting Date",
    sortingField: "accountingDate",
    cell: (item: MatchSetInfo) => item.accountingDate?.split(" ")[0] || "-",
    width: 140,
  },
];

function MatchSetTable({
  items,
  marketplaceId,
  stage,
}: {
  items: MatchSetInfo[];
  marketplaceId: string;
  stage: Stage;
}) {
  const colorMap = buildColorMap(items);
  const columns = COLUMN_DEFINITIONS(marketplaceId, stage, colorMap);
  const { items: sortedItems, collectionProps } = useCollection(items, {
    sorting: {
      defaultState: { sortingColumn: columns[0], isDescending: false },
    },
  });

  return (
    <Table
      {...collectionProps}
      resizableColumns
      items={sortedItems}
      columnDefinitions={columns}
    />
  );
}

export default function MatchSetStatusIndicator({ matchSetData, isDualFlow = false }: Props) {
  const hasNonRoyalty = useMemo(
    () =>
      matchSetData.matchSets.some(
        (ms) => ms.matchKeyType !== MatchKeyType.ROYALTY
      ),
    [matchSetData.matchSets]
  );
  const [modalVisible, setModalVisible] = useState(false);
  const [hideRoyalty, setHideRoyalty] = useState<boolean | null>(null);
  const effectiveHideRoyalty = hideRoyalty ?? hasNonRoyalty;
  const { marketplaceId } = useMarketplaceId();
  const stage = useContext(StageContext);

  if (matchSetData.isLoading) {
    return (
      <StatusIndicator type="loading">Checking match sets...</StatusIndicator>
    );
  }

  if (matchSetData.isError) {
    return (
      <StatusIndicator type="error">Error fetching match sets</StatusIndicator>
    );
  }

  if (matchSetData.total === 0) {
    return <Box color="text-status-inactive">N/A</Box>;
  }

  // When non-royalty match sets exist, exclude royalty ones from the status pill —
  // they stay open for extended periods and obscure real revenue issues.
  // When only royalty match sets exist, include them so the pill isn't "N/A".
  const statusMatchSets = hasNonRoyalty
    ? matchSetData.matchSets.filter(
        (ms) => ms.matchKeyType !== MatchKeyType.ROYALTY
      )
    : matchSetData.matchSets;

  if (statusMatchSets.length === 0) {
    return <Box color="text-status-inactive">N/A</Box>;
  }

  const realMatchSets = statusMatchSets.filter(
    (ms) => ms.state !== MatchSetState.PENDING
  );

  const hasOpen = realMatchSets.some((ms) => ms.state === MatchSetState.OPEN);
  const hasPartial = realMatchSets.some(
    (ms) => ms.state === MatchSetState.PARTIAL
  );

  let label: string;
  let statusType: "success" | "warning" | "pending";
  if (realMatchSets.length === 0) {
    label = "Pending";
    statusType = "pending";
  } else if (hasOpen || hasPartial) {
    label = "Open";
    statusType = "warning";
  } else {
    label = "Closed";
    statusType = "success";
  }

  const boxColor =
    statusType === "success"
      ? "text-status-success"
      : statusType === "warning"
      ? "text-status-warning"
      : "text-status-inactive";

  const statusIndicatorContent = (
    <StatusIndicator type={statusType}>{label}</StatusIndicator>
  );

  const buttonClass =
    statusType === "success"
      ? "match-set-button-success"
      : statusType === "warning"
      ? "match-set-button-warning"
      : "match-set-button-pending";

  return (
    <>
      <Box color={boxColor} display="inline-block">
        <Button
          variant="inline-link"
          onClick={() => {
            const flowType = new URLSearchParams(window.location.search).get(FLOW_PARAM);
            const isRoyaltyFlow = flowType === FLOW_TYPES.DOCUMENT && !isDualFlow;
            setHideRoyalty(isRoyaltyFlow ? false : null);
            setModalVisible(true);
          }}
          ariaLabel="View match set details"
          className={buttonClass}
        >
          {statusIndicatorContent}
        </Button>
      </Box>
      <Modal
        visible={modalVisible}
        onDismiss={() => setModalVisible(false)}
        header={
          <Header
            variant="h2"
            actions={
              <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                <Toggle
                  checked={effectiveHideRoyalty}
                  onChange={({ detail }) => setHideRoyalty(detail.checked)}
                >
                  Hide royalty match sets
                </Toggle>
                <Popover
                  header="Why hide royalty match sets?"
                  content="Royalty match sets remain open until invoices are paid, which can take weeks. This makes every transaction appear 'Open' and obscures real revenue issues. They also span thousands of orders, so an open royalty match set is unlikely to be related to the transaction you're viewing."
                  triggerType="custom"
                  size="medium"
                >
                  <Box color="text-status-info" display="inline">
                    <Icon name="status-info" />
                  </Box>
                </Popover>
              </SpaceBetween>
            }
          >
            Match Set Details
          </Header>
        }
        size="xx-large"
      >
        {(() => {
          const filteredItems = effectiveHideRoyalty
            ? matchSetData.matchSets.filter(
                (ms) => ms.matchKeyType !== MatchKeyType.ROYALTY
              )
            : matchSetData.matchSets;
          return filteredItems.length > 0 ? (
            <MatchSetTable
              items={filteredItems}
              marketplaceId={marketplaceId}
              stage={stage}
            />
          ) : (
            <Box textAlign="center" padding="l" color="text-body-secondary">
              All match sets are royalty match sets. Toggle the filter above to
              view them.
            </Box>
          );
        })()}
      </Modal>
    </>
  );
}
